// SecureEmbed Academic Discovery Portal - Admin Layout
import { Outlet, NavLink, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  Palette,
  BookOpen,
  Shield,
  Users,
  FileText,
  ArrowLeft,
} from 'lucide-react'

interface AdminNavItem {
  to: string
  label: string
  icon: React.ReactNode
  end?: boolean
}

const adminNavItems: AdminNavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: <LayoutDashboard size={18} />, end: true },
  { to: '/admin/branding', label: 'Branding', icon: <Palette size={18} /> },
  { to: '/admin/subjects', label: 'Subjects', icon: <BookOpen size={18} /> },
  { to: '/admin/moderation', label: 'Moderation', icon: <Shield size={18} /> },
  { to: '/admin/users', label: 'Users', icon: <Users size={18} /> },
  { to: '/admin/audit', label: 'Audit Log', icon: <FileText size={18} /> },
]

export default function AdminLayout() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-acad-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin header */}
      <header className="bg-acad-600 text-white px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">Admin Panel</h1>
        <NavLink
          to="/"
          className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          <span>Back to Portal</span>
        </NavLink>
      </header>

      <div className="flex">
        {/* Admin sidebar */}
        <aside className="w-56 min-h-[calc(100vh-64px)] bg-white border-r border-gray-200">
          <nav className="py-4 space-y-0.5">
            {adminNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-acad-50 text-acad-700 border-l-2 border-acad-500'
                      : 'text-gray-600 hover:bg-gray-50 border-l-2 border-transparent'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Back to portal link at bottom */}
          <div className="border-t border-gray-200 py-4">
            <NavLink
              to="/"
              className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 border-l-2 border-transparent"
            >
              <ArrowLeft size={18} />
              <span>Back to Portal</span>
            </NavLink>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 max-w-7xl mx-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
